import {
  signInWithCustomToken,
  onAuthStateChanged,
} from "firebase/auth";
import { useState, useEffect } from "react";
import { getFirebaseAuth } from "../Config/firebaseConfig";
import { postFirebaseToken } from "../Api/postFirebaseToken";

const MAX_SIGN_IN_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

// Track authentication attempts to prevent duplicates
const authAttempts = new Set<string>();

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export class FirebaseAuthService {
  /**
   * Exchange the application JWT for a Firebase custom token and sign in.
   * Firebase Auth UID matches the application user ID for Firestore paths.
   */
  static async signInWithAppAuth(
    userId: string,
    accessToken: string
  ): Promise<void> {
    const auth = getFirebaseAuth();

    if (auth.currentUser?.uid === userId) {
      return;
    }

    const authKey = userId;
    if (authAttempts.has(authKey)) {
      return;
    }

    authAttempts.add(authKey);

    try {
      let lastError: unknown;

      for (let attempt = 0; attempt < MAX_SIGN_IN_ATTEMPTS; attempt += 1) {
        try {
          const { firebase_token } = await postFirebaseToken(accessToken);
          await signInWithCustomToken(auth, firebase_token);
          return;
        } catch (error) {
          lastError = error;
          if (attempt < MAX_SIGN_IN_ATTEMPTS - 1) {
            await sleep(RETRY_BASE_DELAY_MS * (attempt + 1));
          }
        }
      }

      console.error("Error signing in with app auth:", lastError);
      throw lastError;
    } finally {
      authAttempts.delete(authKey);
    }
  }

  /**
   * Sign out and clean up
   */
  static async signOut(): Promise<void> {
    try {
      const auth = getFirebaseAuth();
      if (auth.currentUser) {
        await auth.signOut();
        authAttempts.clear();
      }
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  }

  /**
   * Check if Firebase Auth is signed in
   */
  static isSignedIn(): boolean {
    const auth = getFirebaseAuth();
    return auth?.currentUser !== null;
  }
}

/**
 * Resolves to `true` once the Firebase Auth UID matches the application userId.
 * Uses the SDK's onAuthStateChanged observer instead of polling.
 */
export const useFirebaseAuthReady = (userId: string | undefined) => {
  const [isReady, setIsReady] = useState(() => {
    if (!userId) return false;
    const auth = getFirebaseAuth();
    return auth.currentUser?.uid === userId;
  });

  useEffect(() => {
    if (!userId) {
      setIsReady(false);
      return;
    }

    const auth = getFirebaseAuth();

    if (auth.currentUser?.uid === userId) {
      setIsReady(true);
      return;
    }

    setIsReady(false);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsReady(user?.uid === userId);
    });

    return () => unsubscribe();
  }, [userId]);

  return isReady;
};
